import type { MenuItemContent } from './types.js';

/* ----------------------------------------------------------------------- prices */

/** Symbols people expect on a menu; anything else shows its ISO code. */
const CURRENCY_DISPLAY: Record<string, { symbol: string; before: boolean }> = {
  JOD: { symbol: 'JD', before: false },
  AED: { symbol: 'AED', before: false },
  SAR: { symbol: 'SAR', before: false },
  QAR: { symbol: 'QAR', before: false },
  KWD: { symbol: 'KD', before: false },
  EGP: { symbol: 'EGP', before: false },
  USD: { symbol: '$', before: true },
  EUR: { symbol: '€', before: true },
  GBP: { symbol: '£', before: true },
};

export const MENU_CURRENCIES = Object.keys(CURRENCY_DISPLAY);

/** Three-decimal currencies (JOD, KWD) keep their precision; whole amounts drop decimals. */
const THREE_DECIMAL = new Set(['JOD', 'KWD', 'BHD', 'OMR', 'TND', 'LYD', 'IQD']);

export interface PriceParts {
  symbol: string;
  /** True when the symbol goes before the number ($4.50), false after (4.50 JD). */
  before: boolean;
  whole: string;
  /** The decimals without the point, or null for a whole amount. */
  fraction: string | null;
}

export function formatMenuPriceParts(amount: number, currency: string): PriceParts {
  const code = currency.toUpperCase();
  const number = new Intl.NumberFormat('en', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: THREE_DECIMAL.has(code) ? 3 : 2,
  }).format(amount);
  const [whole, fraction] = number.split('.');
  const display = CURRENCY_DISPLAY[code] ?? { symbol: code, before: false };
  return { symbol: display.symbol, before: display.before, whole: whole!, fraction: fraction ?? null };
}

export function formatMenuPrice(amount: number, currency: string): string {
  const p = formatMenuPriceParts(amount, currency);
  const number = p.fraction ? `${p.whole}.${p.fraction}` : p.whole;
  return p.before ? `${p.symbol}${number}` : `${number} ${p.symbol}`;
}

/** The discount to advertise, or null when there is no real reduction. */
export function discountPercent(item: Pick<MenuItemContent, 'price' | 'discountPrice'>): number | null {
  if (item.discountPrice === null || item.price <= 0 || item.discountPrice >= item.price) {
    return null;
  }
  return Math.round((1 - item.discountPrice / item.price) * 100);
}

/* ----------------------------------------------------------------------- safety */

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export function safeColor(value: string | undefined, fallback: string): string {
  return value && HEX_COLOR.test(value) ? value : fallback;
}

/**
 * Only http(s), blob, data:image and the players' own cache schemes reach an `src`. A bare
 * filesystem path is refused: on Windows it even looks like a URL scheme (`C:`).
 */
export function safeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/^(https?:|blob:|media:|capacitor:|data:image\/)/i.test(url)) return url;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  return null;
}
